<template>
    <div
        class="font-sans bg-white shadow border border-solid border-white rounded-[16px] flex flex-col"
        data-test="flow-row"
    >
        <div
            class="flex py-2 items-center px-4 border-b border-x-0 border-t-0 border-solid rounded-t-[16px] relative justify-between"
            :class="{
                'bg-teal-50 border-teal-100': props.modelValue.status === 'active',
                'bg-slate-50 border-slate-100': props.modelValue.status !== 'active',
            }"
        >
            <div>
                <vx-badge
                    :borders="false"
                    :color="
                        (props.modelValue.status === 'draft' && 'amber') ||
                        (props.modelValue.status === 'paused' && 'danger') ||
                        (props.modelValue.status === 'active' && 'success')
                    "
                    data-test="flow-status"
                >
                    <template v-if="props.modelValue.status === 'draft'">Draft</template>
                    <template v-else-if="props.modelValue.status === 'paused'">Paused</template>
                    <template v-else-if="props.modelValue.status === 'active'">Live</template>
                </vx-badge>

                <template v-if="props.modelValue.playbook">
                    <vx-button
                        v-if="props.modelValue.status == 'draft' || props.modelValue.status == 'paused'"
                        color="transparent"
                        size="sm"
                        @click="emit('launch', props.modelValue.flow_id)"
                        data-test="launch"
                    >
                        Launch
                        <font-awesome-icon widthAuto :icon="faRocketLaunch"></font-awesome-icon>
                    </vx-button>

                    <vx-button
                        v-else-if="props.modelValue.status === 'active'"
                        color="transparent"
                        size="sm"
                        @click="emit('pause', props.modelValue.flow_id)"
                        data-test="pause"
                    >
                        Pause
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-button>
                </template>
            </div>
            <div class="flex items-center gap-6">
                <div
                    class="text-xs font-semibold"
                    :class="{
                        'text-teal-500 ': props.modelValue.status === 'active',
                        'hidden sm:block': Boolean(props.modelValue.playbook),
                    }"
                >
                    {{ props.modelValue.playbook ? 'Playbook' : 'Custom Flow' }}
                </div>
                <div v-if="props.modelValue.playbook" class="flex items-center gap-6">
                    <div
                        v-for="index in 4"
                        :key="index"
                        class="-translate-y-3 flex-col items-center -space-y-2"
                        :class="{
                            'hidden sm:flex': index === 3,
                            flex: index !== 3,
                        }"
                    >
                        <div
                            class="relative h-9 w-[4px] rounded-full bg-linear-to-b from-slate-50 to-slate-600 shadow-[0_2px_5px_#00000022]"
                        ></div>
                        <div
                            class="aspect-square w-2.5 rounded-full bg-slate-500 shadow-[0_1px_2px_#000000bb_inset]"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
        <div class="px-6 py-6 flex flex-col rounded-b-[16px] relative gap-4 grow lg:flex-row lg:items-center lg:py-4">
            <div class="">
                <h3 class="block text-lg font-bold text-slate-800 truncate leading-normal m-0" data-test="flow-name">
                    {{ props.modelValue.name }}
                </h3>
                <div class="text-xs text-slate-500">
                    <div class="line-clamp-3 lg:line-clamp-2">
                        {{ props.modelValue.notes }}
                    </div>
                    <div v-if="props.modelValue.playbook" class="mt-2 xl:mt-1">
                        <strong>Playbook:</strong> {{ playbookName(props.modelValue.playbook) }}
                    </div>
                </div>
            </div>
            <div v-if="props.modelValue.playbook" class="w-40 mx-auto shrink-0 lg:mx-0 lg:w-32">
                <component
                    :is="playbookArt(props.modelValue.playbook)"
                    v-if="playbookArt(props.modelValue.playbook)"
                ></component>
            </div>

            <div class="flex gap-2 mt-auto lg:my-0 lg:ml-auto lg:grow-0">
                <vx-dropdown teleport placement="left">
                    <vx-button class="w-11" type="button" color="muted" outline size="md">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item @click.prevent="removeModal = true" type="button" data-test="remove-flow">
                            Delete <font-awesome-icon widthAuto :icon="faTrashCan"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>

                <vx-button
                    color="muted"
                    :outline="true"
                    :href="
                        '/automation/flows/' +
                        props.modelValue.flow_id +
                        (!props.modelValue.playbook ? '/resources' : '')
                    "
                    class="grow"
                >
                    Edit
                    <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <flow-delete
            v-model:visible="removeModal"
            :flowId="props.modelValue.flow_id"
            @deleted="emit('deleted')"
        ></flow-delete>
    </div>
</template>

<script setup>
import { faEllipsis, faFilePen, faPause, faRocketLaunch, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { VxBadge, VxButton, VxDropdown, VxDropdownItem } from '@voxie/frontend-components';
import { playbookArt, playbookName } from './playbook/playbook';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FlowDelete from './FlowDelete.vue';
import { ref } from 'vue';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const removeModal = ref(false);

const emit = defineEmits(['launch', 'pause', 'deleted']);
</script>
